/**
 * Iter-130b — Clean multi-image continuity print sheet.
 *
 * One responsibility: take a list of state-entry rows (with their
 * resolved scene + look + notes context) and print them to a single
 * sheet with one of three layouts:
 *
 *   - "one"     : one image per page (full bleed, biggest reference)
 *   - "two"     : two images per page (side-by-side comparison)
 *   - "contact" : compact contact-sheet grid (3 columns)
 *
 * The print metadata is intentionally minimal per pilot spec:
 *
 *   Scene · Look · Notes
 *
 * No project name, no internal IDs, no "captured by", no timestamp,
 * no system clutter. The point of these sheets is to clip to a
 * trolley mirror or hand to a leg of the team — they have to be
 * scan-and-go.
 *
 * Implementation mirrors the existing single-image `openPrintWindow`
 * in ImageDetail.jsx — a hidden iframe + srcdoc + `contentWindow.print()`
 * + cleanup. We wait for every <img> to settle before triggering
 * print so iPad Safari doesn't render the dialog before the bitmap
 * decodes.
 */

import { mediaUrl, entryImageSrc } from "./media";

function esc(s) {
    return String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function resolveImageSrc(projectId, entry, apiBase = "/projects") {
    // Prefer the highest-quality variant available — GridFS original
    // when present, else the legacy inline data URL.
    if (entry?.media_id) return mediaUrl(projectId, entry.media_id, "original", { apiBase });
    return entry?.image_data || "";
}

function sceneLabel(scene) {
    if (!scene) return null;
    const num = scene.scene_number || "";
    const ep = scene.episode ? `${scene.episode}.` : "";
    const numLabel = num ? `Sc ${ep}${num}` : "";
    const heading = scene.heading || "";
    if (numLabel && heading) return `${numLabel} — ${heading}`;
    return numLabel || heading || null;
}

/**
 * Build the HTML body for the given entries + layout.
 * Each card is wrapped in `.card` (page-break controlled by CSS).
 */
function buildCardsHtml(items, layout) {
    return items
        .map((it, idx) => {
            const src = it.src || "";
            const scene = it.sceneLabelText
                ? `<div class="meta-row"><span class="meta-k">Scene</span><span class="meta-v">${esc(it.sceneLabelText)}</span></div>`
                : "";
            // Look hierarchy: per-entry look beats scene-level look.
            const lookValue = it.look || it.sceneLook || null;
            const look = lookValue
                ? `<div class="meta-row"><span class="meta-k">Look</span><span class="meta-v">${esc(lookValue)}</span></div>`
                : "";
            const notes = it.notes
                ? `<div class="meta-row notes-row"><span class="meta-k">Notes</span><span class="meta-v">${esc(it.notes)}</span></div>`
                : "";
            const meta =
                scene || look || notes
                    ? `<div class="meta">${scene}${look}${notes}</div>`
                    : "";
            return `<section class="card layout-${esc(layout)}" data-idx="${idx}">
  <div class="image-wrap">
    <img class="print-img" src="${esc(src)}" alt="" />
  </div>
  ${meta}
</section>`;
        })
        .join("\n");
}

/**
 * Layout-specific CSS. Each layout gives the card a fixed share of the
 * page so the browser's print engine paginates cleanly.
 */
function layoutCss(layout) {
    if (layout === "two") {
        return `
  .card { width: 48%; display: inline-block; vertical-align: top; margin: 0 0.75% 14px; page-break-inside: avoid; }
  .card:nth-child(2n) { margin-right: 0; }
  .card:nth-child(2n+1) { margin-left: 0; }
  .card .image-wrap img { max-height: 38vh; }
  .card .meta { font-size: 11px; }
  .card .meta-k { width: 56px; font-size: 9px; }
`;
    }
    if (layout === "contact") {
        return `
  .card { width: 32%; display: inline-block; vertical-align: top; margin: 0 0.4% 10px; page-break-inside: avoid; }
  .card .image-wrap img { max-height: 24vh; }
  .card .meta { font-size: 10px; }
  .card .meta-k { width: 40px; font-size: 8px; }
  .card .notes-row .meta-v { max-height: 2.6em; overflow: hidden; }
`;
    }
    // Default — one image per page.
    return `
  .card { width: 100%; page-break-after: always; }
  .card:last-child { page-break-after: auto; }
  .card .image-wrap img { max-height: 70vh; }
`;
}

/**
 * Resolve { src, sceneLabelText, sceneLook, look, notes } from
 * (entry, scene, charNotes) inputs. Caller passes already-loaded
 * scene + per-pair notes; this helper just shapes them into the
 * sheet rows.
 */
export function buildPrintItem({ projectId, entry, scene, notes, apiBase = "/projects" }) {
    return {
        src: resolveImageSrc(projectId, entry, apiBase),
        sceneLabelText: sceneLabel(scene),
        sceneLook: scene?.look || null,
        look: entry?.look || null,
        notes: notes || entry?.note || null,
    };
}

/**
 * Open a new hidden-iframe print job for the given items.
 * `layout` ∈ {"one","two","contact"}.
 */
export function printContinuitySheet({ items, layout = "one" }) {
    if (!items || items.length === 0) return;
    const safeLayout = ["one", "two", "contact"].includes(layout) ? layout : "one";
    const cards = buildCardsHtml(items, safeLayout);
    // Clean title is intentionally generic — we don't surface the
    // project name on the printed sheet per the pilot spec, so the
    // PDF "Save As" suggestion stays neutral too.
    const title = "Continuity";
    const css = `
* { box-sizing: border-box; }
html, body {
  margin: 0; padding: 0; background: #fff; color: #111;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
main { padding: 14mm 12mm; }
.card { background: #fff; }
.image-wrap {
  width: 100%;
  display: flex; align-items: center; justify-content: center;
  background: #f6f6f6; border: 1px solid #eee; border-radius: 2px;
  padding: 6px; margin-bottom: 8px;
}
.image-wrap img { width: 100%; height: auto; object-fit: contain; max-width: 100%; }
.meta { font-size: 12px; }
.meta-row { display: flex; gap: 8px; padding: 3px 0; border-bottom: 1px solid #eee; align-items: flex-start; }
.meta-row:last-child { border-bottom: none; }
.meta-k {
  flex: 0 0 70px;
  font-size: 9px; text-transform: uppercase; letter-spacing: 0.1em;
  color: #777; padding-top: 2px;
}
.meta-v { flex: 1; white-space: pre-wrap; word-break: break-word; color: #111; }
${layoutCss(safeLayout)}
@media print {
  @page { margin: 12mm; }
  body { padding: 0; }
  main { padding: 0; }
  .image-wrap { background: transparent; border: none; padding: 0; }
}
`;
    const html = `<!doctype html><html lang="en"><head>
<meta charset="utf-8" />
<title>${esc(title)}</title>
<style>${css}</style>
</head>
<body><main>${cards}</main></body></html>`;

    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("data-testid", "continuity-sheet-print-frame");
    iframe.setAttribute("title", "OnSetFlow continuity print");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.style.opacity = "0";
    document.body.appendChild(iframe);

    let printed = false;
    const trigger = () => {
        if (printed) return;
        printed = true;
        try {
            const w = iframe.contentWindow;
            if (!w) {
                window.print();
                return;
            }
            w.focus();
            w.print();
        } catch {
            window.print();
        }
        // Tear the iframe down after a small delay so the print
        // dialog has a moment to capture the frame.
        setTimeout(() => {
            try {
                document.body.removeChild(iframe);
            } catch {
                /* ignore */
            }
        }, 1500);
    };

    iframe.addEventListener("load", () => {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        const imgs = doc ? Array.from(doc.querySelectorAll(".print-img")) : [];
        if (imgs.length === 0) {
            trigger();
            return;
        }
        let remaining = imgs.length;
        const onSettle = () => {
            remaining -= 1;
            if (remaining <= 0) trigger();
        };
        for (const img of imgs) {
            if (img.complete) {
                onSettle();
            } else {
                img.addEventListener("load", onSettle);
                img.addEventListener("error", onSettle);
            }
        }
        // Safety net — print after 4s even if an image hangs.
        setTimeout(() => trigger(), 4000);
    });

    iframe.srcdoc = html;
}

/* ================================================================
 * Iter-158.13.2.20f.18 — Scene & Character A4 Continuity Sheets.
 *
 * Purpose: a Hair & Makeup department can print a scene-wide or
 * character-wide continuity sheet, three-hole-punch it and file it
 * in a physical continuity binder. This is Carla's live workflow —
 * Daniel still works substantially off paper. The design brief is
 * "clean, professional, legible on a photocopier, no ink-heavy
 * decoration, no app chrome."
 *
 * Architecture: builds an entirely fresh A4-native HTML document
 * (production identity, scene/character subject, continuity blocks,
 * photos, notes, per-pair look) and hands it to the same iframe
 * pipeline `printContinuitySheet` uses. We do NOT refactor the
 * iter-130b sheet — this is a parallel A4 report path with a
 * dedicated stylesheet.
 *
 * Data comes verbatim from the existing continuity endpoints. No
 * new API, no data mutation, no permission change.
 * ================================================================ */

// Formats a date without pulling in a heavy library. Falls back to
// ISO if the environment misbehaves (jsdom, iOS locale quirks).
function _formattedNow() {
    try {
        const d = new Date();
        return d.toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return new Date().toISOString();
    }
}

// Composite Scene label "1.7 — HALLWAY DAY" that we render as the
// subject line at the top of every Scene sheet and as the per-scene
// block header on Character sheets.
function _sceneComposite(scene) {
    if (!scene) return "";
    const ep = scene.episode ? `${scene.episode}.` : "";
    const num = scene.scene_number ? `${ep}${scene.scene_number}` : "";
    const heading = scene.heading || "";
    if (num && heading) return `${num} — ${heading}`;
    return num || heading || "Scene";
}

function _characterSubject(character) {
    if (!character) return "Character";
    const num = character.character_number ? `#${character.character_number} ` : "";
    return `${num}${character.name || "Character"}`.trim();
}

// Preferred print image: delegate to `entryImageSrc`, the proven
// resolver used everywhere else in the app. It (a) absolutises the
// backend `image_url`/`thumbnail_url` hint against
// REACT_APP_BACKEND_URL — critical because `srcdoc` iframes have
// no base URL and cannot resolve `/api/...` relative paths — and
// (b) appends the JWT `?token=` query string so the browser's raw
// `<img>` request (which can't send Authorization headers)
// authenticates against the media endpoint. Iter-158.13.2.20f.19c
// UAT B fix: Character sheet showed broken-image placeholders in
// browser Print / Save-as-PDF because the previous resolver
// returned the relative hint verbatim, which the iframe could not
// resolve or authenticate.
function _defaultImgResolver({ projectId, entry, apiBase }) {
    return entryImageSrc(projectId, entry, "display", apiBase);
}

// Shared A4 stylesheet. Deliberately spartan: black on white, thin
// hairline dividers, no filled backgrounds, no icons, no rounded
// panels. Photocopier-safe.
function _a4Css({ headerLeft, headerRight, footerLeft }) {
    // We bake header/footer strings into the CSS `@page` string so
    // Chromium and Safari can render them per printed page. Strip
    // characters that could either break the CSS parser (quote,
    // backslash, newline) or leak angle-bracket noise into the
    // literal text rendered by the @page area — the browser prints
    // whatever we put here verbatim, so we keep it plain-text.
    const cssEscape = (s) => String(s ?? "").replace(/["\\\r\n<>]/g, " ");
    const hl = cssEscape(headerLeft);
    const hr = cssEscape(headerRight);
    const fl = cssEscape(footerLeft);
    return `
* { box-sizing: border-box; }
html, body {
  margin: 0;
  padding: 0;
  background: #fff;
  color: #111;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 11pt;
  line-height: 1.35;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
main { padding: 0; }
h1, h2, h3 { margin: 0; padding: 0; font-weight: 700; color: #000; }
.sheet-header {
  border-bottom: 1.5px solid #000;
  padding-bottom: 6mm;
  margin-bottom: 6mm;
}
.sheet-production {
  font-size: 9pt;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #333;
  margin-bottom: 2mm;
}
.sheet-subject {
  font-size: 18pt;
  line-height: 1.15;
  color: #000;
  margin-bottom: 2mm;
  word-break: break-word;
}
.sheet-meta {
  font-size: 10pt;
  color: #222;
  margin-top: 1mm;
}
.sheet-meta .k {
  font-size: 8pt;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #555;
  margin-right: 2mm;
}
.sheet-desc {
  font-size: 10pt;
  color: #222;
  margin-top: 3mm;
  white-space: pre-wrap;
  word-break: break-word;
}
.block {
  page-break-inside: avoid;
  break-inside: avoid;
  border-top: 1px solid #999;
  padding: 4mm 0 6mm;
  margin: 0;
}
.block:first-of-type { border-top: none; padding-top: 0; }
.block-header {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 3mm;
  margin-bottom: 2mm;
}
.block-num {
  font-weight: 700;
  font-size: 14pt;
  color: #000;
  min-width: 12mm;
}
.block-title {
  font-weight: 700;
  font-size: 13pt;
  color: #000;
  flex: 1;
  min-width: 40mm;
  word-break: break-word;
}
.block-actor {
  font-size: 10pt;
  color: #333;
  font-style: italic;
}
.block-tag {
  font-size: 8pt;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #444;
  border: 1px solid #999;
  padding: 0.5mm 2mm;
  border-radius: 2px;
}
.block-meta {
  font-size: 10pt;
  color: #222;
  margin: 1mm 0;
}
.block-meta-row {
  display: flex;
  gap: 3mm;
  align-items: flex-start;
  padding: 0.5mm 0;
}
.block-meta-row .k {
  flex: 0 0 22mm;
  font-size: 8pt;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #555;
  padding-top: 0.5mm;
}
.block-meta-row .v {
  flex: 1;
  color: #111;
  white-space: pre-wrap;
  word-break: break-word;
}
.entries-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4mm;
  margin-top: 3mm;
}
.entry {
  page-break-inside: avoid;
  break-inside: avoid;
  border: 1px solid #bbb;
  padding: 2mm;
}
.entry-img {
  width: 100%;
  aspect-ratio: 4 / 3;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 2mm;
}
.entry-img img.print-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}
.entry-img.empty {
  color: #999;
  font-size: 9pt;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  border: 1px dashed #bbb;
}
.entry-meta {
  font-size: 9pt;
  color: #111;
}
.entry-meta .row {
  display: flex;
  gap: 2mm;
  padding: 0.4mm 0;
}
.entry-meta .row .k {
  flex: 0 0 12mm;
  font-size: 7.5pt;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #666;
}
.entry-meta .row .v {
  flex: 1;
  white-space: pre-wrap;
  word-break: break-word;
}
.no-entries {
  font-size: 9pt;
  color: #555;
  font-style: italic;
  margin: 2mm 0 1mm;
}
@page {
  size: A4;
  margin: 14mm 12mm 18mm;
  @top-left {
    content: "${hl}";
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
    font-size: 8pt;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #555;
  }
  @top-right {
    content: "${hr}";
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
    font-size: 8pt;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #555;
  }
  @bottom-left {
    content: "${fl}";
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
    font-size: 8pt;
    color: #666;
  }
  @bottom-right {
    content: "Page " counter(page);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
    font-size: 8pt;
    color: #666;
  }
}
@media print {
  body { padding: 0; }
  .entry-img { background: transparent; }
}
`;
}

// The shared print launcher — hidden iframe, wait for every <img>
// to settle, then trigger contentWindow.print(). Mirrors the iter-
// 130b lifecycle byte-for-byte so we don't drift from a pattern
// that is already proven on iPad Safari. Testid is distinct so
// Jest can differentiate the A4 sheet from the iter-130b sheet.
function _launchPrintIframe({ html, testid }) {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("data-testid", testid);
    iframe.setAttribute("title", "OnSetFlow continuity print");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.style.opacity = "0";
    document.body.appendChild(iframe);

    let printed = false;
    const trigger = () => {
        if (printed) return;
        printed = true;
        try {
            const w = iframe.contentWindow;
            if (!w) {
                window.print();
                return;
            }
            w.focus();
            w.print();
        } catch {
            window.print();
        }
        setTimeout(() => {
            try {
                document.body.removeChild(iframe);
            } catch {
                /* ignore */
            }
        }, 1500);
    };

    iframe.addEventListener("load", () => {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        const imgs = doc ? Array.from(doc.querySelectorAll(".print-img")) : [];
        if (imgs.length === 0) {
            trigger();
            return;
        }
        let remaining = imgs.length;
        const onSettle = () => {
            remaining -= 1;
            if (remaining <= 0) trigger();
        };
        for (const img of imgs) {
            if (img.complete) {
                onSettle();
            } else {
                img.addEventListener("load", onSettle);
                img.addEventListener("error", onSettle);
            }
        }
        // Safety net — never leave the user hanging if a bitmap
        // stalls on GridFS. Print after 5s regardless. Slightly
        // more generous than the iter-130b sheet because A4
        // reports can carry 50+ photos.
        setTimeout(() => trigger(), 5000);
    });

    iframe.srcdoc = html;
}

// Renders one continuity entry (a photo + its per-entry look/note).
function _entryHtml(entry, imgSrc) {
    const src = imgSrc || "";
    const img = src
        ? `<div class="entry-img"><img class="print-img" src="${esc(src)}" alt="" /></div>`
        : `<div class="entry-img empty">No image</div>`;
    const rows = [];
    if (entry?.look) {
        rows.push(`<div class="row"><span class="k">Look</span><span class="v">${esc(entry.look)}</span></div>`);
    }
    if (entry?.note) {
        rows.push(`<div class="row"><span class="k">Note</span><span class="v">${esc(entry.note)}</span></div>`);
    }
    const meta = rows.length ? `<div class="entry-meta">${rows.join("")}</div>` : "";
    return `<div class="entry">${img}${meta}</div>`;
}

/**
 * Iter-158.13.2.20f.18 — Print an A4 continuity sheet for one Scene
 * covering every attached Character.
 *
 * @param {object}   opts
 * @param {string}   opts.productionName        Production title for header/footer.
 * @param {object}   opts.scene                 The scene record.
 * @param {Array}    opts.characters            `characters` array from
 *                                              `/scenes/:id/continuity`
 *                                              — each item: `{ character,
 *                                              notes, entries[] }`.
 * @param {string}   opts.projectId             For media URL resolution.
 * @param {string}   opts.apiBase               `useApiBase()` value.
 * @param {Function} [opts.imgResolver]         Optional injection for
 *                                              tests. Signature:
 *                                              `({ projectId, entry,
 *                                              apiBase }) => string`.
 */
export function printSceneContinuitySheet({
    productionName,
    scene,
    characters,
    projectId,
    apiBase = "/projects",
    imgResolver = _defaultImgResolver,
} = {}) {
    const production = productionName || "Production";
    const subjectLine = `Scene ${_sceneComposite(scene)}`;
    const printedAt = _formattedNow();
    const sceneMetaBits = [];
    if (scene?.int_ext) sceneMetaBits.push(scene.int_ext);
    if (scene?.time_of_day) sceneMetaBits.push(scene.time_of_day);
    const sceneMetaHtml = sceneMetaBits.length
        ? `<div class="sheet-meta"><span class="k">Setting</span>${esc(sceneMetaBits.join(" · "))}</div>`
        : "";
    const sceneLookHtml = scene?.look
        ? `<div class="sheet-meta"><span class="k">Scene Look</span>${esc(scene.look)}</div>`
        : "";
    const sceneDescHtml = scene?.description
        ? `<div class="sheet-desc">${esc(scene.description)}</div>`
        : "";

    const blocks = (characters || []).map((b) => {
        const c = b?.character || {};
        const num = c.character_number
            ? `<span class="block-num">#${esc(c.character_number)}</span>`
            : `<span class="block-num">—</span>`;
        const actor = c.actor ? `<span class="block-actor">${esc(c.actor)}</span>` : "";
        const teamTag = c.team === "crowd" ? `<span class="block-tag">Crowd</span>` : "";
        const notesHtml = b?.notes
            ? `<div class="block-meta-row"><span class="k">Notes</span><span class="v">${esc(b.notes)}</span></div>`
            : "";
        const entries = Array.isArray(b?.entries) ? b.entries : [];
        let entriesHtml = "";
        if (entries.length === 0) {
            entriesHtml = `<p class="no-entries">No continuity photos on file for this character in this scene.</p>`;
        } else {
            entriesHtml = `<div class="entries-grid">${entries
                .map((e) => _entryHtml(e, imgResolver({ projectId, entry: e, apiBase })))
                .join("")}</div>`;
        }
        return `<section class="block" data-block-kind="character">
  <header class="block-header">
    ${num}
    <span class="block-title">${esc(c.name || "Character")}</span>
    ${actor}
    ${teamTag}
  </header>
  ${notesHtml ? `<div class="block-meta">${notesHtml}</div>` : ""}
  ${entriesHtml}
</section>`;
    }).join("\n");

    const headerLeft = `${production} — ${subjectLine}`;
    const headerRight = "H&M Continuity";
    const footerLeft = `Printed ${printedAt}`;
    const css = _a4Css({ headerLeft, headerRight, footerLeft });

    const body = `<main>
  <section class="sheet-header">
    <div class="sheet-production">${esc(production)}</div>
    <h1 class="sheet-subject">${esc(subjectLine)}</h1>
    ${sceneMetaHtml}
    ${sceneLookHtml}
    ${sceneDescHtml}
  </section>
  ${blocks || `<p class="no-entries">No characters attached to this scene yet.</p>`}
</main>`;

    const html = `<!doctype html><html lang="en"><head>
<meta charset="utf-8" />
<title>${esc(`${production} — ${subjectLine}`)}</title>
<style>${css}</style>
</head><body>${body}</body></html>`;

    _launchPrintIframe({ html, testid: "scene-continuity-sheet-frame" });
}

/**
 * Iter-158.13.2.20f.18 — Print an A4 continuity sheet for one
 * Character covering every attached Scene.
 *
 * @param {object}   opts
 * @param {string}   opts.productionName
 * @param {object}   opts.character            The character record.
 * @param {Array}    opts.blocks               `scenes` array from
 *                                             `/characters/:id/continuity`
 *                                             — each item: `{ scene,
 *                                             notes, entries[],
 *                                             not_in_script }`.
 * @param {object}   [opts.fittings]           Optional fittings block
 *                                             `{ entries[], entry_count }`
 *                                             — rendered first when
 *                                             present so H&M can flip
 *                                             straight to prep photos.
 * @param {string}   opts.projectId
 * @param {string}   opts.apiBase
 * @param {Function} [opts.imgResolver]
 */
export function printCharacterContinuitySheet({
    productionName,
    character,
    blocks,
    fittings,
    projectId,
    apiBase = "/projects",
    imgResolver = _defaultImgResolver,
} = {}) {
    const production = productionName || "Production";
    const subjectLine = _characterSubject(character);
    const printedAt = _formattedNow();
    const actorHtml = character?.actor
        ? `<div class="sheet-meta"><span class="k">Actor</span>${esc(character.actor)}</div>`
        : "";
    const teamHtml = character?.team === "crowd"
        ? `<div class="sheet-meta"><span class="k">Team</span>Crowd</div>`
        : "";
    const notesHtml = character?.notes
        ? `<div class="sheet-desc">${esc(character.notes)}</div>`
        : "";

    const fittingsBlock = (fittings?.entries && fittings.entries.length > 0)
        ? (() => {
              const entriesHtml = `<div class="entries-grid">${fittings.entries
                  .map((e) => _entryHtml(e, imgResolver({ projectId, entry: e, apiBase })))
                  .join("")}</div>`;
              return `<section class="block" data-block-kind="fittings">
  <header class="block-header">
    <span class="block-num">FIT</span>
    <span class="block-title">Fittings — Unassigned</span>
    <span class="block-tag">${fittings.entries.length} photo${fittings.entries.length === 1 ? "" : "s"}</span>
  </header>
  ${entriesHtml}
</section>`;
          })()
        : "";

    const sceneBlocks = (blocks || []).map((b) => {
        const s = b?.scene || {};
        const ep = s.episode ? `${s.episode}.` : "";
        const numLabel = s.scene_number ? `${ep}${s.scene_number}` : "—";
        const heading = s.heading || "";
        const metaBits = [];
        if (s.int_ext) metaBits.push(s.int_ext);
        if (s.time_of_day) metaBits.push(s.time_of_day);
        const settingHtml = metaBits.length
            ? `<div class="block-meta-row"><span class="k">Setting</span><span class="v">${esc(metaBits.join(" · "))}</span></div>`
            : "";
        const sceneLookHtml = s.look
            ? `<div class="block-meta-row"><span class="k">Scene Look</span><span class="v">${esc(s.look)}</span></div>`
            : "";
        const pairNotesHtml = b?.notes
            ? `<div class="block-meta-row"><span class="k">Notes</span><span class="v">${esc(b.notes)}</span></div>`
            : "";
        const metaRowsHtml = (settingHtml || sceneLookHtml || pairNotesHtml)
            ? `<div class="block-meta">${settingHtml}${sceneLookHtml}${pairNotesHtml}</div>`
            : "";
        const notInScriptTag = b?.not_in_script
            ? `<span class="block-tag">Not in script</span>`
            : "";
        const omittedTag = s.omitted ? `<span class="block-tag">Omitted</span>` : "";
        const entries = Array.isArray(b?.entries) ? b.entries : [];
        let entriesHtml = "";
        if (entries.length === 0) {
            entriesHtml = `<p class="no-entries">No continuity photos on file for this scene.</p>`;
        } else {
            entriesHtml = `<div class="entries-grid">${entries
                .map((e) => _entryHtml(e, imgResolver({ projectId, entry: e, apiBase })))
                .join("")}</div>`;
        }
        return `<section class="block" data-block-kind="scene">
  <header class="block-header">
    <span class="block-num">${esc(numLabel)}</span>
    <span class="block-title">${esc(heading || "Scene")}</span>
    ${notInScriptTag}
    ${omittedTag}
  </header>
  ${metaRowsHtml}
  ${entriesHtml}
</section>`;
    }).join("\n");

    const headerLeft = `${production} — ${subjectLine}`;
    const headerRight = "H&M Continuity";
    const footerLeft = `Printed ${printedAt}`;
    const css = _a4Css({ headerLeft, headerRight, footerLeft });

    const body = `<main>
  <section class="sheet-header">
    <div class="sheet-production">${esc(production)}</div>
    <h1 class="sheet-subject">${esc(subjectLine)}</h1>
    ${actorHtml}
    ${teamHtml}
    ${notesHtml}
  </section>
  ${fittingsBlock}
  ${sceneBlocks || `<p class="no-entries">This character has no scenes on file yet.</p>`}
</main>`;

    const html = `<!doctype html><html lang="en"><head>
<meta charset="utf-8" />
<title>${esc(`${production} — ${subjectLine}`)}</title>
<style>${css}</style>
</head><body>${body}</body></html>`;

    _launchPrintIframe({ html, testid: "character-continuity-sheet-frame" });
}

/**
 * Test-only export — surfaces the raw HTML the two A4 print
 * functions above would inject into the iframe. Jest string-lock
 * regressions rely on this so they can assert markup without
 * spinning up an iframe. Not consumed by production code.
 */
export const __printSheetInternals = {
    buildSceneContinuityHtml({ productionName, scene, characters, projectId, apiBase, imgResolver }) {
        const production = productionName || "Production";
        const subjectLine = `Scene ${_sceneComposite(scene)}`;
        const printedAt = _formattedNow();
        const resolver = imgResolver || _defaultImgResolver;
        const sceneMetaBits = [];
        if (scene?.int_ext) sceneMetaBits.push(scene.int_ext);
        if (scene?.time_of_day) sceneMetaBits.push(scene.time_of_day);
        const sceneMetaHtml = sceneMetaBits.length
            ? `<div class="sheet-meta"><span class="k">Setting</span>${esc(sceneMetaBits.join(" · "))}</div>`
            : "";
        const sceneLookHtml = scene?.look
            ? `<div class="sheet-meta"><span class="k">Scene Look</span>${esc(scene.look)}</div>`
            : "";
        const sceneDescHtml = scene?.description
            ? `<div class="sheet-desc">${esc(scene.description)}</div>`
            : "";
        const blocks = (characters || []).map((b) => {
            const c = b?.character || {};
            const num = c.character_number
                ? `<span class="block-num">#${esc(c.character_number)}</span>`
                : `<span class="block-num">—</span>`;
            const actor = c.actor ? `<span class="block-actor">${esc(c.actor)}</span>` : "";
            const teamTag = c.team === "crowd" ? `<span class="block-tag">Crowd</span>` : "";
            const notesHtml = b?.notes
                ? `<div class="block-meta-row"><span class="k">Notes</span><span class="v">${esc(b.notes)}</span></div>`
                : "";
            const entries = Array.isArray(b?.entries) ? b.entries : [];
            const entriesHtml = entries.length === 0
                ? `<p class="no-entries">No continuity photos on file for this character in this scene.</p>`
                : `<div class="entries-grid">${entries.map((e) => _entryHtml(e, resolver({ projectId, entry: e, apiBase }))).join("")}</div>`;
            return `<section class="block" data-block-kind="character">
  <header class="block-header">
    ${num}
    <span class="block-title">${esc(c.name || "Character")}</span>
    ${actor}
    ${teamTag}
  </header>
  ${notesHtml ? `<div class="block-meta">${notesHtml}</div>` : ""}
  ${entriesHtml}
</section>`;
        }).join("\n");
        const headerLeft = `${production} — ${subjectLine}`;
        const headerRight = "H&M Continuity";
        const footerLeft = `Printed ${printedAt}`;
        const css = _a4Css({ headerLeft, headerRight, footerLeft });
        const body = `<main>
  <section class="sheet-header">
    <div class="sheet-production">${esc(production)}</div>
    <h1 class="sheet-subject">${esc(subjectLine)}</h1>
    ${sceneMetaHtml}
    ${sceneLookHtml}
    ${sceneDescHtml}
  </section>
  ${blocks || `<p class="no-entries">No characters attached to this scene yet.</p>`}
</main>`;
        return `<!doctype html><html lang="en"><head>
<meta charset="utf-8" />
<title>${esc(`${production} — ${subjectLine}`)}</title>
<style>${css}</style>
</head><body>${body}</body></html>`;
    },
    buildCharacterContinuityHtml({ productionName, character, blocks, fittings, projectId, apiBase, imgResolver }) {
        const production = productionName || "Production";
        const subjectLine = _characterSubject(character);
        const printedAt = _formattedNow();
        const resolver = imgResolver || _defaultImgResolver;
        const actorHtml = character?.actor
            ? `<div class="sheet-meta"><span class="k">Actor</span>${esc(character.actor)}</div>`
            : "";
        const teamHtml = character?.team === "crowd"
            ? `<div class="sheet-meta"><span class="k">Team</span>Crowd</div>`
            : "";
        const notesHtml = character?.notes
            ? `<div class="sheet-desc">${esc(character.notes)}</div>`
            : "";
        const fittingsBlock = (fittings?.entries && fittings.entries.length > 0)
            ? (() => {
                  const entriesHtml = `<div class="entries-grid">${fittings.entries.map((e) => _entryHtml(e, resolver({ projectId, entry: e, apiBase }))).join("")}</div>`;
                  return `<section class="block" data-block-kind="fittings">
  <header class="block-header">
    <span class="block-num">FIT</span>
    <span class="block-title">Fittings — Unassigned</span>
    <span class="block-tag">${fittings.entries.length} photo${fittings.entries.length === 1 ? "" : "s"}</span>
  </header>
  ${entriesHtml}
</section>`;
              })()
            : "";
        const sceneBlocks = (blocks || []).map((b) => {
            const s = b?.scene || {};
            const ep = s.episode ? `${s.episode}.` : "";
            const numLabel = s.scene_number ? `${ep}${s.scene_number}` : "—";
            const heading = s.heading || "";
            const metaBits = [];
            if (s.int_ext) metaBits.push(s.int_ext);
            if (s.time_of_day) metaBits.push(s.time_of_day);
            const settingHtml = metaBits.length
                ? `<div class="block-meta-row"><span class="k">Setting</span><span class="v">${esc(metaBits.join(" · "))}</span></div>`
                : "";
            const sceneLookHtml = s.look
                ? `<div class="block-meta-row"><span class="k">Scene Look</span><span class="v">${esc(s.look)}</span></div>`
                : "";
            const pairNotesHtml = b?.notes
                ? `<div class="block-meta-row"><span class="k">Notes</span><span class="v">${esc(b.notes)}</span></div>`
                : "";
            const metaRowsHtml = (settingHtml || sceneLookHtml || pairNotesHtml)
                ? `<div class="block-meta">${settingHtml}${sceneLookHtml}${pairNotesHtml}</div>`
                : "";
            const notInScriptTag = b?.not_in_script ? `<span class="block-tag">Not in script</span>` : "";
            const omittedTag = s.omitted ? `<span class="block-tag">Omitted</span>` : "";
            const entries = Array.isArray(b?.entries) ? b.entries : [];
            const entriesHtml = entries.length === 0
                ? `<p class="no-entries">No continuity photos on file for this scene.</p>`
                : `<div class="entries-grid">${entries.map((e) => _entryHtml(e, resolver({ projectId, entry: e, apiBase }))).join("")}</div>`;
            return `<section class="block" data-block-kind="scene">
  <header class="block-header">
    <span class="block-num">${esc(numLabel)}</span>
    <span class="block-title">${esc(heading || "Scene")}</span>
    ${notInScriptTag}
    ${omittedTag}
  </header>
  ${metaRowsHtml}
  ${entriesHtml}
</section>`;
        }).join("\n");
        const headerLeft = `${production} — ${subjectLine}`;
        const headerRight = "H&M Continuity";
        const footerLeft = `Printed ${printedAt}`;
        const css = _a4Css({ headerLeft, headerRight, footerLeft });
        const body = `<main>
  <section class="sheet-header">
    <div class="sheet-production">${esc(production)}</div>
    <h1 class="sheet-subject">${esc(subjectLine)}</h1>
    ${actorHtml}
    ${teamHtml}
    ${notesHtml}
  </section>
  ${fittingsBlock}
  ${sceneBlocks || `<p class="no-entries">This character has no scenes on file yet.</p>`}
</main>`;
        return `<!doctype html><html lang="en"><head>
<meta charset="utf-8" />
<title>${esc(`${production} — ${subjectLine}`)}</title>
<style>${css}</style>
</head><body>${body}</body></html>`;
    },
};
